'use client';
import { useState } from 'react';
import { TypeMediaCollectionProduct } from '@/core/generated-types';

export interface TypeVideoPosition {
  partId: string;
  chapterId: string;
  subChapterId: string;
  muxVideoId: string;
}

export function useMediaCollectionStreamingQueue(
  mediaCollectionProduct: TypeMediaCollectionProduct,
  initialVideoId?: string | undefined,
) {
  const [videoPositions, setVideoPositions] = useState(
    _generateStreamingQueue(mediaCollectionProduct, initialVideoId),
  );

  const [activeVideoPosition, onDeckVideoPosition] = videoPositions;
  const activeSubChapter = _findSubChapter(
    activeVideoPosition,
    mediaCollectionProduct,
  );
  const onDeckSubChapter = _findSubChapter(
    onDeckVideoPosition,
    mediaCollectionProduct,
  );

  function regenerateStreamingQueueFromVideo(subChapterId: string) {
    setVideoPositions(
      _generateStreamingQueue(mediaCollectionProduct, subChapterId),
    );
  }

  function reportActiveVideoCompleted() {
    // when the Media Collection is completed, we'll leave the last video in the player
    const isLastVideo = videoPositions.length === 1;
    if (isLastVideo) {
      return;
    }

    const tempCoursePositionsQueue = [...videoPositions];
    // removes completed video from queue
    tempCoursePositionsQueue.shift();

    setVideoPositions(tempCoursePositionsQueue);
  }

  return {
    activeVideoPosition,
    onDeckVideoPosition,
    activeSubChapter,
    onDeckSubChapter,
    reportActiveVideoCompleted,
    regenerateStreamingQueueFromVideo,
  };
}

export function _findSubChapter(
  position: TypeVideoPosition,
  mediaCollectionProduct: TypeMediaCollectionProduct,
) {
  if (!position) return null;

  const mediaCollectionPart = mediaCollectionProduct.fields.parts.find(
    (part) => part.sys.id === position.partId,
  );

  const mediaCollectionChapter = mediaCollectionPart?.fields?.chapters?.find(
    (chapter) => chapter.sys.id === position.chapterId,
  );

  return mediaCollectionChapter?.fields?.subChapters?.find(
    (subChapter) => subChapter.sys.id === position.subChapterId,
  );
}

export function _generateStreamingQueue(
  mediaCollection: TypeMediaCollectionProduct,
  initialMuxVideoId?: string | undefined,
) {
  const fullCourseQueue = mediaCollection.fields.parts.flatMap((part) => {
    return part.fields.chapters.flatMap((chapter) => {
      return (chapter.fields?.subChapters || []).map(
        (subChapter) =>
          ({
            partId: part.sys.id,
            chapterId: chapter.sys.id,
            subChapterId: subChapter.sys.id,
            muxVideoId: subChapter.fields.video.sys.id,
          }) as TypeVideoPosition,
      );
    });
  });

  if (initialMuxVideoId) {
    const positionIdx = fullCourseQueue.findIndex(
      (position) => position.muxVideoId === initialMuxVideoId,
    );

    if (positionIdx !== -1) {
      return fullCourseQueue.slice(positionIdx);
    }
  }

  return fullCourseQueue;
}

export default useMediaCollectionStreamingQueue;
